import { useState } from 'react';
import CustomButton from '../../reusable/CustomButton';
import CustomDivider from '../../reusable/CustomDivider';
import Tag from '../../reusable/Tag';
import { TranscriptDetails, TranscripsCategoriesKeys, Transcript } from '../../../interfaces/TranscriptsInterface';
import useSchools from '../../hooks/useSchools';
import { useNavigate } from 'react-router-dom';
import GoBackIcon from '../../assets/transcript-selection-arrow-left.svg';
import { useTranscriptSelectionOutletContext } from '../../hooks/useTranscriptSelectionOutletContext';
import { Checkbox, FormControlLabel, CircularProgress, Box } from '@mui/material';
import { selectTranscript, formatToTitleCase } from '../utilityFunctions';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import { USER_ROLES, TRANSCRIPTS_CATEGORIES_LABELS } from '../../../constants';
import { trimTo2Decimals } from '../../../utils';
import TranscriptDetailsPrintButton from '../print/TranscriptDetailsPrintButton';

import './TranscriptDetailsHeader.css';

interface TranscriptDetailsHeaderProps {
  data: TranscriptDetails;
  category: TranscripsCategoriesKeys;
  transcript: Transcript;
}

const summaryData = {
  gpa: 'GPA',
  credits: 'Credits',
  course_intensity_score: 'Course intensity',
  race: 'Race/ethnicity',
  gender: 'Gender',
  student_with_disabilities: 'Disability Status',
  english_learner: 'English language learner status',
  low_income: 'Low income',
  profile_names: 'Profile',
};

function TranscriptDetailsHeader({ data, category, transcript }: TranscriptDetailsHeaderProps) {
  const { transcriptSelectionSchool, schools } = useSchools();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const navigate = useNavigate();

  const userRole = userDetails?.role;
  const isFacilitator = userRole === USER_ROLES.FACILITATOR;
  const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
  const isAdmin = userRole === USER_ROLES.XQ_ADMIN;

  const {
    selectedTranscriptsSummaryResponse: { selectedTranscriptsSummary, setSelectedTranscriptsSummary },
    workflowStatus,
    refreshWorkflowStatus,
  } = useTranscriptSelectionOutletContext();

  const canEdit =
    (workflowStatus === 'TRANSCRIPT_SELECTION_IN_PROGRESS' || workflowStatus === 'TRANSCRIPT_SELECTION_NOT_STARTED') &&
    (isFacilitator || isLeadFacilitator);

  const showCheckbox = canEdit || isAdmin;
  const selectedTranscriptsIdsByCategory = selectedTranscriptsSummary.selectedTranscriptsByCategory[category];

  const isSelected = Boolean(selectedTranscriptsIdsByCategory.find((t) => t.id === transcript.id));
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectTranscript = async () => {
    setIsLoading(true);
    try {
      await selectTranscript(
        userDetails,
        category,
        transcript,
        isSelected,
        transcriptSelectionSchool,
        handleOpenErrorModal,
        setSelectedTranscriptsSummary,
        workflowStatus,
        refreshWorkflowStatus
      );
    } finally {
      setIsLoading(false);
    }
  };
  const selectedSchoolName = schools.data?.find((school) => school.id === transcriptSelectionSchool)?.name || '';

  const summaryDataValuesMap: Record<string, any> = {
    gpa: trimTo2Decimals(data.gpa),
    profile_names: transcript.profile_names ? formatToTitleCase(transcript.profile_names.join(', ')) : 'N/A',
    ...Object.fromEntries(
      Object.keys(summaryData)
        .filter((key) => key !== 'gpa' && key !== 'profile_names')
        .map((key) => [
          key,
          data[key as keyof TranscriptDetails]?.toString().toLowerCase() || transcript[key as keyof Transcript],
        ])
    ),
  };

  return (
    <div className="transcript-details-header-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back"></img>}
        iconPosition="start"
        buttonAction={() => navigate('/transcripts')}
      />
      <div className="transcript-details-header">
        <h2>Transcript - Student {transcript.school_student_number}</h2>
        <Tag
          type="info"
          text={TRANSCRIPTS_CATEGORIES_LABELS[category]}
          size="medium"
          className="upload-progress-info"
        />
        <div className="buttons-container">
          {showCheckbox && (
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <FormControlLabel
                label="Select this transcript"
                control={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Checkbox checked={isSelected} onChange={handleSelectTranscript} disabled={!canEdit || isLoading} />
                    {isLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Box>
          )}
          <TranscriptDetailsPrintButton data={data} />
        </div>
      </div>
      <CustomDivider orientation="horizontal" size="medium" />
      <div className="transcript-details-summary">
        <div className="category">
          <p>School</p>
          <span>{selectedSchoolName}</span>
        </div>
        {Object.entries(summaryData).map(([key, value]) => (
          <div className="category" key={key}>
            <p>{value}</p>
            <span className="category-value">{summaryDataValuesMap[key]}</span>
          </div>
        ))}
      </div>
      <CustomDivider orientation="horizontal" size="medium" />
    </div>
  );
}

export default TranscriptDetailsHeader;
